import { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Bot } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export async function streamSSE(path, body, onDelta) {
  const res = await fetch(`${API}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok || !res.body) throw new Error("request failed");
  const reader = res.body.getReader();
  const dec = new TextDecoder();
  let buf = "";
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });
    const parts = buf.split("\n\n");
    buf = parts.pop();
    for (const part of parts) {
      const line = part.trim();
      if (!line.startsWith("data:")) continue;
      const payload = line.slice(5).trim();
      if (payload === "[DONE]") return;
      try {
        const j = JSON.parse(payload);
        if (j.delta) onDelta(j.delta);
      } catch {}
    }
  }
}

const WELCOME =
  "Dobrý den! Jsem AI poradce RESTART PRO. Zeptejte se na cokoliv — cenu, termín nebo postup stavby.";

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([{ role: "assistant", content: WELCOME }]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const listRef = useRef(null);
  const sessionRef = useRef(null);

  if (!sessionRef.current) {
    let sid = null;
    try {
      sid = localStorage.getItem("rp_chat_session");
      if (!sid) {
        sid = crypto.randomUUID();
        localStorage.setItem("rp_chat_session", sid);
      }
    } catch {
      sid = crypto.randomUUID();
    }
    sessionRef.current = sid;
  }

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [messages, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setInput("");
    setSending(true);
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    try {
      await streamSSE("/chat", { session_id: sessionRef.current, message: text }, (delta) => {
        setMessages((m) => {
          const next = [...m];
          const last = next[next.length - 1];
          next[next.length - 1] = { ...last, content: last.content + delta };
          return next;
        });
      });
    } catch {
      setMessages((m) => {
        const next = [...m];
        next[next.length - 1] = {
          role: "assistant",
          content: "Omlouváme se, poradce je dočasně nedostupný. Zavolejte nám prosím na +420 777 940 118.",
        };
        return next;
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="chat-open-button"
        aria-label="Otevřít AI poradce"
        className="fixed bottom-6 left-6 z-50 flex items-center justify-center w-14 h-14 rounded-full bg-gold text-black shadow-lg shadow-gold/25 hover:brightness-110 hover:scale-105 transition-all duration-300"
      >
        {open ? <X size={24} /> : <MessageCircle size={24} />}
      </button>

      {open && (
        <div
          data-testid="chat-panel"
          className="fixed bottom-24 left-4 sm:left-6 z-50 w-[min(92vw,380px)] h-[70vh] max-h-[560px] flex flex-col rounded-2xl border border-gold/25 bg-ink shadow-2xl overflow-hidden"
        >
          <div className="flex items-center gap-3 px-4 py-3 border-b border-white/10 bg-card2">
            <span className="flex items-center justify-center w-8 h-8 rounded-full bg-gold/15 border border-gold/40 text-gold">
              <Bot size={16} />
            </span>
            <div>
              <p className="text-sm font-semibold text-zinc-100">AI poradce RESTART PRO</p>
              <p className="text-[11px] text-zinc-500">Odpovídá z dat webu — ceny, termíny, postup</p>
            </div>
          </div>

          <div ref={listRef} className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="chat-messages">
            {messages.map((m, i) => (
              <div
                key={i}
                data-testid={`chat-message-${m.role}-${i}`}
                className={`max-w-[85%] rounded-xl px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                  m.role === "user"
                    ? "ml-auto bg-gold text-black"
                    : "bg-card2 border border-white/10 text-zinc-200"
                }`}
              >
                {m.content}
                {m.role === "assistant" && sending && i === messages.length - 1 && m.content === "" && (
                  <span className="inline-block w-2 h-4 bg-gold/70 animate-pulse" />
                )}
              </div>
            ))}
          </div>

          <div className="p-3 border-t border-white/10 flex gap-2">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Napište dotaz…"
              maxLength={1000}
              data-testid="chat-input"
              className="flex-1 bg-card2 border border-white/10 focus:border-gold/50 rounded-lg px-3 py-2.5 text-sm text-zinc-100 placeholder:text-zinc-600 outline-none transition-colors"
            />
            <button
              type="button"
              onClick={send}
              disabled={sending || !input.trim()}
              data-testid="chat-send-button"
              aria-label="Odeslat zprávu"
              className="flex items-center justify-center w-10 h-10 rounded-lg bg-gold text-black hover:brightness-110 transition disabled:opacity-40"
            >
              <Send size={16} />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
